/* eslint-disable */
import { useState, useEffect, useRef } from 'react';
import { Button, Select, Collapse, Input, Row, Col, Card, Divider } from 'antd';
import moment from 'moment';

import callApi from '../Api/config';
import { getRole } from '../service/CookieManager';
import { formatIsoDate } from '../utils/functions';
import { documitraManualPaymentStatusOption } from '../constants/AppConstant';
import { toastError, toastSuccess } from '../service/Toast';
import { Copy, DollarSign, FileText, ImagePlus, Stamp, Truck, Zap } from 'lucide-react';
import PaymentVoucher from './PaymentVoucher';
import { InvertFillButton } from './InvertFillButton';
import HorizontalTabsSec, {
  HorizontalTabsSecPanel,
} from './HorizontalTabNavigation/HorizontalTabsSec';
import RefundRequestDetails from './RefundRequestDetails/RefundRequestDetails';
import RefundTrackingHistory from './RefundTrackingHistory';
import type { RefundRequest } from '../pages/RefundRequests';
import { getApostilleLabel } from '../utils/apostille';

const NAVY = '#004195';

const paymentStatusSelectOptions = documitraManualPaymentStatusOption.map((opt: any) => ({
  label: opt.name,
  value: opt.value,
}));

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span
      style={{
        fontSize: '15px',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        color: '#53657f',
      }}
    >
      {label}
    </span>
    <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{value || '—'}</span>
  </div>
);

const statusStyles: Record<string, { bg: string; color: string; ring: string }> = {
  success: { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' },
  paid: { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' },
  pending: { bg: '#fffbeb', color: '#b45309', ring: 'rgba(180,83,9,0.2)' },
  failed: { bg: '#fff1f2', color: '#be123c', ring: 'rgba(190,18,60,0.2)' },
  'payment done': { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' },
  'payment successful': { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' },
  completed: { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' },
  'payment pending': { bg: '#fffbeb', color: '#b45309', ring: 'rgba(180,83,9,0.2)' },
  'refund issued': { bg: '#fdf2f8', color: '#9d174d', ring: 'rgba(157,23,77,0.2)' },
  approved: { bg: '#dcfce7', color: '#15803d', ring: 'rgba(21,128,61,0.25)' },
  rejected: { bg: '#fee2e2', color: '#b91c1c', ring: 'rgba(185,28,28,0.25)' },
  refunded: { bg: '#e0e7ff', color: '#4338ca', ring: 'rgba(67,56,202,0.25)' },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status)
    return <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>—</span>;
  const style = statusStyles[status.toLowerCase()] ?? {
    bg: '#f1f5f9',
    color: '#475569',
    ring: 'rgba(71,85,105,0.2)',
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        width: 'fit-content',
        alignItems: 'center',
        borderRadius: '999px',
        padding: '2px 10px',
        fontSize: '16px',
        fontWeight: 600,
        textTransform: 'capitalize',
        background: style.bg,
        color: style.color,
        boxShadow: `inset 0 0 0 1px ${style.ring}`,
        border: `1px solid ${style.ring}`,
      }}
    >
      {status}
    </span>
  );
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: '14px',
  border: '1px solid #e2e8f0',
  background: '#fff',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '14px',
  paddingBottom: '14px',
  borderBottom: '1px solid #f1f5f9',
};

const sectionHeaderLabelStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'black',
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  columnGap: '14px',
  rowGap: '14px',
};

const sectionDividerStyle: React.CSSProperties = {
  marginTop: '24px',
  paddingTop: '16px',
  borderTop: '1px dashed #e2e8f0',
};

const refundFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: '20px',
};

const PaymentDetails = (props: any) => {
  const {
    paymentDetails,
    userName,
    userEmail,
    formDetails,
    onPaymentStatusUpdate,
    paymentStatusLoading,
    hidePaymentVoucher,
    orderRefund,
  } = props;
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState<string | undefined>(undefined);
  const [voucherRefreshTrigger, setVoucherRefreshTrigger] = useState(0);
  const previousTabRef = useRef<string>('payment-information');

  const [inlineCurrentRefund, setInlineCurrentRefund] = useState<any>(orderRefund);

  const handleTabChange = (activeId: string) => {
    const previousTab = previousTabRef.current;
    if (
      (previousTab === 'payment-information' && activeId === 'payment-voucher') ||
      (previousTab === 'payment-voucher' && activeId === 'payment-information')
    ) {
      setVoucherRefreshTrigger(prev => prev + 1);
    }
    previousTabRef.current = activeId;
  };

  const refundStatusBadge = (status?: string) => {
    const key = String(status || 'PENDING').toUpperCase();
    const backgroundColor =
      key === 'APPROVED'
        ? '#52c41a'
        : key === 'REJECTED'
          ? '#f5222d'
          : key === 'REFUNDED'
            ? '#13c2c2'
            : '#faad14';
    const label = key === 'PENDING' ? 'Requested' : key.charAt(0) + key.slice(1).toLowerCase();
    return (
      <span
        style={{
          display: 'inline-block',
          marginLeft: 6,
          padding: '1px 6px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          color: '#fff',
          backgroundColor,
          verticalAlign: 'middle',
        }}
      >
        {label}
      </span>
    );
  };

  useEffect(() => {
    const current = paymentDetails?.paymentDetails?.paymentStatus;
    setPendingPaymentStatus(current ? String(current) : undefined);
  }, [paymentDetails?.id, paymentDetails?.paymentDetails?.paymentStatus]);

  const inlineRequestedAmount = Number(
    inlineCurrentRefund?.amountPaid ?? inlineCurrentRefund?.amount ?? 0
  );
  const inlineCurrency = inlineCurrentRefund?.currency || 'USD';
  const appliedCoupon = [
    paymentDetails?.appliedCoupon,
    formDetails?.appliedCoupon,
    paymentDetails?.paymentDetails?.appliedCoupon,
    formDetails?.paymentDetails?.appliedCoupon,
    inlineCurrentRefund?.appliedCoupon,
  ].find(value => value != null && String(value).trim() !== '');
  const couponDiscount = [
    paymentDetails?.couponDiscount,
    formDetails?.couponDiscount,
    paymentDetails?.paymentDetails?.couponDiscount,
    formDetails?.paymentDetails?.couponDiscount,
    inlineCurrentRefund?.couponDiscount,
  ].find(value => value != null);

  const inlineFormatDate = (dateValue?: string | number | Date) => {
    if (dateValue == null || dateValue === '') return '--';

    const timestamp =
      typeof dateValue === 'string' && /^\d{10,13}$/.test(dateValue)
        ? Number(dateValue)
        : dateValue;
    const normalizedTimestamp =
      typeof timestamp === 'number' && timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = moment(normalizedTimestamp);

    return date.isValid() ? date.format('DD/MM/YYYY') : '--';
  };

  const orderRefundId = orderRefund?.id || orderRefund?.refundRequestId;
  const orderRefundStatus = String(orderRefund?.status || '')
    .trim()
    .toUpperCase();

  useEffect(() => {
    setInlineCurrentRefund(orderRefund);

    // PENDING too, so the internal eligibility step shows in the refund history.
    if (
      !orderRefundId ||
      !['PENDING', 'APPROVED', 'REJECTED', 'REFUNDED'].includes(orderRefundStatus)
    ) {
      return;
    }

    let cancelled = false;
    const hydrateRefundDetails = async () => {
      try {
        const response: any = await callApi(`/v1/refund/details/${orderRefundId}`, 'GET');
        if (cancelled) return;
        const payload = response?.data?.data || response?.data;
        const detailList = payload?.details || payload?.transactionDetails || [];
        const latest = Array.isArray(detailList) ? detailList[0] : null;
        if (!latest && !payload?.refund) return;

        setInlineCurrentRefund((prev: any) => ({
          ...prev,
          ...(payload?.refund || {}),
          amountPaid: Number(payload?.refund?.amount ?? prev?.amountPaid) || prev?.amountPaid,
          refundAmount:
            payload?.refund?.refundAmount != null
              ? Number(payload.refund.refundAmount)
              : prev?.refundAmount,
          refundDetails: latest
            ? {
                refundDate: latest.dateOfRefund,
                refundAmount: Number(latest.amountOfRefund),
                refundMethod: latest.methodOfRefund,
                refundedBy: latest.refundedBy,
                refundedAt: latest.createdAt,
                notes: latest.remarks || '',
                confirmationDocumentUrl: latest.confirmationDocumentUrl,
                confirmationDocumentName: latest.confirmationDocumentName,
              }
            : prev?.refundDetails,
        }));
      } catch (err) {
        console.error('Failed to hydrate refund details', err);
      }
    };

    hydrateRefundDetails();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once per refund id/status
  }, [orderRefundId, orderRefundStatus]);
  const inlineShowApprovalReadOnly = ['APPROVED', 'REJECTED', 'REFUNDED'].includes(
    String(inlineCurrentRefund?.status || '')
      .trim()
      .toUpperCase()
  );
  const inlineShowRefundedSection =
    String(inlineCurrentRefund?.status || '')
      .trim()
      .toUpperCase() === 'REFUNDED';

  const inlineCardTitleStyle: React.CSSProperties = {
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: '#004195',
  };

  const inlineDisplayRefundId =
    inlineCurrentRefund?.refundRequestId || inlineCurrentRefund?.id || '';

  const handleCopyRefundId = async () => {
    if (!inlineDisplayRefundId) {
      toastError('No Refund ID to copy');
      return;
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(String(inlineDisplayRefundId));
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = String(inlineDisplayRefundId);
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toastSuccess('Refund ID copied to clipboard');
    } catch (err) {
      console.error('Failed to copy Refund ID:', err);
      toastError('Failed to copy Refund ID');
    }
  };

  const currentUserRole: any = getRole();
  //eslint-disable-next-line
  console.log(typeof paymentDetails?.apostilled?.selected);

  const savedApostilleQuantity = Number(paymentDetails?.apostilled?.quantity);
  // Legacy Apostille orders stored only the total at the previous $60 rate.
  const apostilleQuantity =
    savedApostilleQuantity > 0
      ? savedApostilleQuantity
      : Number(paymentDetails?.apostilled?.price || 0) / 60;

  // Helper function to check if payment is completed
  const isPaymentCompleted = (paymentStatus: string | undefined): boolean => {
    if (!paymentStatus) return false;
    const status = paymentStatus.toLowerCase().trim();
    return status === 'payment done' || status === 'payment successful' || status === 'completed';
  };

  const handleDownloadForm = (paymentDetails: any) => {
    if (!paymentDetails) {
      return;
    }

    const escapeHtml = (value: any) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const paidAmount =
      isPaymentCompleted(paymentDetails?.paymentDetails?.paymentStatus) &&
      paymentDetails?.paymentDetails?.amountPaid
        ? paymentDetails?.paymentDetails?.amountPaid
        : '--';

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Payment Details</title></head>
<body style="padding: 20px; font-family: Inter, sans-serif;">
  <div style="padding: 20px">
    <div>${escapeHtml(userName)} Payment Details</div>
    <div style="display: flex; justify-content: space-between; font-size: 22px">
      <div><h4>Applicant's Name</h4><p>${escapeHtml(userName || '-')}</p></div>
      <div><h4>Applicant's Email</h4><p>${escapeHtml(userEmail || '-')}</p></div>
    </div>
    <div style="display: flex; justify-content: space-between; font-size: 22px">
      <div><h4>Payment Mode</h4><p>${escapeHtml(paymentDetails?.paymentDetails?.provider || '-')}</p></div>
      <div><h4>Payment Status</h4><p>${escapeHtml(paymentDetails?.paymentDetails?.paymentStatus || '-')}</p></div>
      <div><h4>Payment Date</h4><p>${escapeHtml(paymentDetails.paymentDate || '-')}</p></div>
    </div>
    <div style="display: flex; justify-content: space-between; font-size: 22px">
      <div><h4>Order Id</h4><p>${escapeHtml(paymentDetails.orderId || '-')}</p></div>
    </div>
    <div style="display: flex; justify-content: center;">
      <table border="1" style="width: 100%; border-collapse: collapse; text-align: center; font-size: 24px;">
        <tr><th>Title</th><th>Details</th></tr>
        <tr><td rowspan="2">Selected Add Ons</td><td>Quantity: ${escapeHtml(paymentDetails.photoEnhancement?.quantity || 'No Add On Selected')}</td></tr>
        <tr><td>Price: ${escapeHtml(paymentDetails.photoEnhancement?.price || 'No Add On Selected')}</td></tr>
        <tr><td rowspan="2">Selected Plan</td><td>Name: ${escapeHtml(paymentDetails.pricingPlan?.title || 'No plan selected')}</td></tr>
        <tr><td>Price per member: ${escapeHtml(paymentDetails.pricingPlan?.discountedPrice || 'No plan selected')}</td></tr>
        <tr><td>Selected Package</td><td>${escapeHtml(paymentDetails.packageName || '-')}</td></tr>
        <tr><td>Applying From</td><td>${escapeHtml(formDetails?.applyingFrom || '-')}</td></tr>
        <tr><td>Citizenship</td><td>${escapeHtml(formDetails?.citizenship || '-')}</td></tr>
        <tr><td>Destination</td><td>${escapeHtml(formDetails?.destination || '-')}</td></tr>
        <tr><td rowspan="2">Coupon Details</td><td>Coupon applied: ${escapeHtml(paymentDetails.appliedCoupon || 'No Coupon Applied')}</td></tr>
        <tr><td>Coupon Discount: ${escapeHtml(paymentDetails.couponDiscount || 'No Coupon Applied')}</td></tr>
      </table>
    </div>
    <div style="display: flex; justify-content: space-between; border: 0.2px solid gray; border-radius: 5px; margin: 30px 0; padding: 0 10px; font-weight: 700; font-size: 20px;">
      <p>Convenience Fee</p><p>$${escapeHtml(paymentDetails.convenienceFee || '-')}</p>
    </div>
    <div style="display: flex; justify-content: space-between; border: 0.2px solid gray; border-radius: 5px; margin: 30px 0; padding: 0 10px; font-weight: 700; font-size: 20px;">
      <p>Paid Amount</p><p>$${escapeHtml(paidAmount)}</p>
    </div>
  </div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-details-${paymentDetails.orderId || 'order'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isGroupOrder = paymentDetails?.groupMemberCount !== undefined;

  // Purchase summary is for ALL members of the order. Package, Expedite and Courier are priced
  // per member (backend multiplies by member count); Photo Enhancement and Apostille prices are
  // already stored as totals. The cards on the right keep the original single-member prices.
  const toAmount = (value: any) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatUsd = (value: number) => `USD ${value.toFixed(2)}`;
  const memberCount = Math.max(
    1,
    toAmount(
      paymentDetails?.groupMemberCount ??
        paymentDetails?.memberCount ??
        (Array.isArray(formDetails?.members) ? formDetails.members.length : 0)
    ) || 1
  );
  const memberCountLabel = `${memberCount} member${memberCount === 1 ? '' : 's'}`;
  const packagePricePerMember = toAmount(
    paymentDetails?.pricingPlan?.discountedPricePerMember ||
      paymentDetails?.pricingPlan?.discountedPrice
  );
  const packageTotal = packagePricePerMember * memberCount;
  const photoEnhancementQuantity = Math.max(
    1,
    toAmount(paymentDetails?.photoEnhancement?.quantity) || 1
  );
  const photoEnhancementTotal = toAmount(paymentDetails?.photoEnhancement?.price);
  const photoEnhancementUnit = photoEnhancementTotal / photoEnhancementQuantity;
  const expeditePerMember = toAmount(paymentDetails?.expedite?.price);
  const expediteTotal = expeditePerMember * memberCount;
  const apostilledTotal = toAmount(paymentDetails?.apostilled?.price);
  const apostilleLabel = getApostilleLabel(paymentDetails?.apostilled);
  const apostilledUnitPrice =
    apostilleQuantity > 0 ? apostilledTotal / apostilleQuantity : apostilledTotal;
  const courierPerMember = toAmount(paymentDetails?.courierService?.price);
  const courierTotal = courierPerMember * memberCount;
  const summaryTotal =
    toAmount(paymentDetails?.paymentDetails?.amountPaid) ||
    toAmount(paymentDetails?.payableOfferPrice);
  const perMemberHintStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    textAlign: 'right',
  };

  const hasAddOns =
    paymentDetails?.photoEnhancement?.selected ||
    paymentDetails?.expedite?.selected ||
    paymentDetails?.apostilled?.selected ||
    paymentDetails?.courierService?.selected;

  return (
    <div key={paymentDetails?.id} style={{ marginBottom: '20px' }}>
      <HorizontalTabsSec
        defaultActiveId="payment-information"
        hideTabs={hidePaymentVoucher}
        onTabChange={handleTabChange}
      >
        <HorizontalTabsSecPanel id="payment-information" label="Payment Information">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
            }}
          >
            <div style={cardStyle}>
              {/* Order Overview Section */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 20,
                    paddingBottom: 10,
                    borderBottom: '1px solid rgb(241, 245, 249)',
                  }}
                >
                  <div style={sectionHeaderLabelStyle}>Payment Status & Details</div>
                  <div>
                    {currentUserRole === 'Super Admin' && onPaymentStatusUpdate && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #86efac',
                          overflow: 'hidden',
                          backgroundColor: '#f0fdf4',
                          color: '#15803d',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
                        }}
                      >
                        <div
                          style={{
                            minWidth: '140px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#64748b',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                            }}
                          >
                            Payment Status:
                          </span>
                        </div>
                        <Select
                          style={{ width: 300, fontSize: '16px' }}
                          value={pendingPaymentStatus}
                          onChange={value => setPendingPaymentStatus(value)}
                          options={paymentStatusSelectOptions}
                          size="middle"
                          placeholder="Select Payment Status"
                        />
                        <Button
                          type="primary"
                          onClick={() => onPaymentStatusUpdate(pendingPaymentStatus)}
                          loading={paymentStatusLoading}
                          style={{
                            fontSize: '16px',
                            height: '32px',
                            padding: '0 20px',
                            backgroundColor: NAVY,
                            borderColor: NAVY,
                            borderRadius: '8px',
                            fontWeight: 600,
                          }}
                        >
                          Update
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={fieldGridStyle}>
                {/* <Field label="Order ID" value={paymentDetails?.orderId} /> */}
                <Field
                  label="Payment Status"
                  value={<StatusBadge status={paymentDetails?.paymentDetails?.paymentStatus} />}
                />
                <Field
                  label="Payment Date"
                  value={
                    paymentDetails?.paymentDate ? formatIsoDate(paymentDetails.paymentDate) : '—'
                  }
                />
                <Field label="Payment Method" value={paymentDetails?.paymentDetails?.provider} />
                <Field label="Order Date" value={formatIsoDate(paymentDetails?.orderDate)} />
              </div>

              <div style={sectionDividerStyle}>
                {/* <div style={sectionHeaderStyle}>
              <span style={sectionHeaderLabelStyle}>Plan Details</span>
            </div> */}
                <div style={fieldGridStyle}>
                  {!isGroupOrder && paymentDetails?.packageName && (
                    <Field label="Selected Package" value={paymentDetails?.packageName} />
                  )}

                  {!isGroupOrder && formDetails?.applyingFrom && (
                    <Field label="Applying From" value={formDetails?.applyingFrom} />
                  )}
                  {!isGroupOrder && formDetails?.citizenship && (
                    <Field label="Citizenship" value={formDetails?.citizenship} />
                  )}
                  {!isGroupOrder && formDetails?.destination && (
                    <Field label="Destination" value={formDetails?.destination} />
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                }}
              >
                {/* Coupon & Fees Section */}
                {!isGroupOrder &&
                  (paymentDetails?.appliedCoupon || paymentDetails?.paymentDetails) && (
                    <div>
                      {/* <div style={sectionHeaderStyle}>
                  <span style={sectionHeaderLabelStyle}>Order Breakdown</span>
                </div> */}

                      <div style={fieldGridStyle}>
                        {/* {paymentDetails?.appliedCoupon ? (
                        <>
                          <Field
                            label="Coupon Applied"
                            value={paymentDetails?.appliedCoupon}
                          />
                          <Field
                            label="Coupon Discount"
                            value={`USD ${paymentDetails?.couponDiscount}`}
                          />
                        </>
                      ) : (
                        <Field
                          label="Coupon"
                          value={
                            <span style={{ fontSize: '16px', fontWeight: 600, color: '#94a3b8' }}>
                              No Coupon Applied
                            </span>
                          }
                        />
                      )}

                      {paymentDetails?.paymentDetails && (
                        <>
                          <Field
                            label="Convenience Fee"
                            value={`USD ${paymentDetails?.convenienceFee || '--'}`}
                          />
                          <Field
                            label="Paid Amount"
                            value={
                              isPaymentCompleted(paymentDetails?.paymentDetails?.paymentStatus) &&
                                paymentDetails?.paymentDetails?.amountPaid
                                ? `USD ${paymentDetails?.paymentDetails?.amountPaid}`
                                : '—'
                            }
                          />
                        </>
                      )} */}

                        {/* Payment Calculation Breakdown */}
                        {paymentDetails?.paymentDetails && (
                          <div
                            style={{
                              gridColumn: 'span 4',
                              marginTop: '20px',
                              padding: '16px',
                              borderRadius: '8px',
                              background: 'rgba(0,48,135,0.03)',
                              border: '1px solid rgba(0,48,135,0.1)',
                            }}
                          >
                            <div
                              style={{
                                marginBottom: '12px',
                                fontSize: '15px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                                color: NAVY,
                              }}
                            >
                              Purchase Summary
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {/* Members covered by this order */}
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span
                                  style={{ fontSize: '15px', color: '#1e293b', fontWeight: 600 }}
                                >
                                  Members
                                </span>
                                <span
                                  style={{
                                    padding: '1px 10px',
                                    borderRadius: '999px',
                                    border: '1px solid #bfdbfe',
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                  }}
                                >
                                  {memberCountLabel}
                                </span>
                              </div>
                              <span
                                style={{ height: '1px', width: '100%', backgroundColor: '#E5E7EB' }}
                              />

                              {/* Package Cost (price per member x members) */}
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span
                                  style={{
                                    fontSize: '15px',
                                    color: '#1e293b',
                                    fontWeight: 600,
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center',
                                  }}
                                >
                                  Package Cost{' '}
                                  <span>
                                    {memberCount > 1 && (
                                      <span style={perMemberHintStyle}>
                                        ( {formatUsd(packagePricePerMember)} × {memberCount} )
                                      </span>
                                    )}
                                  </span>
                                </span>

                                <span
                                  style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}
                                >
                                  {formatUsd(packageTotal)}
                                </span>
                              </div>

                              {/* Photo Enhancement */}
                              {paymentDetails?.photoEnhancement?.selected && (
                                <>
                                  <span
                                    style={{
                                      height: '1px',
                                      width: '100%',
                                      backgroundColor: '#E5E7EB',
                                    }}
                                  />

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span
                                      style={{
                                        fontSize: '15px',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                      }}
                                    >
                                      Photo Enhancement
                                      <span>
                                        {photoEnhancementQuantity > 1 && (
                                          <span style={perMemberHintStyle}>
                                            ( {formatUsd(photoEnhancementUnit)} × {memberCount} )
                                          </span>
                                        )}
                                      </span>
                                    </span>

                                    <span
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                      }}
                                    >
                                      + {formatUsd(photoEnhancementTotal)}
                                    </span>
                                  </div>
                                </>
                              )}

                              {/* Expedite */}
                              {paymentDetails?.expedite?.selected && (
                                <>
                                  <span
                                    style={{
                                      height: '1px',
                                      width: '100%',
                                      backgroundColor: '#E5E7EB',
                                    }}
                                  />

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span
                                      style={{
                                        fontSize: '15px',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                      }}
                                    >
                                      Expedite{' '}
                                      <span>
                                        {' '}
                                        {memberCount > 1 && (
                                          <span style={perMemberHintStyle}>
                                            ( {formatUsd(expeditePerMember)} × {memberCount} )
                                          </span>
                                        )}
                                      </span>
                                    </span>

                                    <span
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                      }}
                                    >
                                      + {formatUsd(expediteTotal)}
                                    </span>
                                  </div>
                                </>
                              )}

                              {/* Apostilled */}
                              {paymentDetails?.apostilled?.selected && (
                                <>
                                  <span
                                    style={{
                                      height: '1px',
                                      width: '100%',
                                      backgroundColor: '#E5E7EB',
                                    }}
                                  />

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span
                                      style={{
                                        fontSize: '15px',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                      }}
                                    >
                                      {apostilleLabel}
                                      <span>
                                        {apostilleQuantity > 1 && (
                                          <span style={perMemberHintStyle}>
                                            ( {formatUsd(apostilledUnitPrice)} × {apostilleQuantity}{' '}
                                            )
                                          </span>
                                        )}
                                      </span>
                                    </span>

                                    <span
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                      }}
                                    >
                                      + {formatUsd(apostilledTotal)}
                                    </span>
                                  </div>
                                </>
                              )}

                              {/* Courier Service */}
                              {paymentDetails?.courierService?.selected && (
                                <>
                                  <span
                                    style={{
                                      height: '1px',
                                      width: '100%',
                                      backgroundColor: '#E5E7EB',
                                    }}
                                  />

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span
                                      style={{
                                        fontSize: '15px',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                      }}
                                    >
                                      Courier Service
                                      <span>
                                        {memberCount > 1 && (
                                          <span style={perMemberHintStyle}>
                                            ( {formatUsd(courierPerMember)} × {memberCountLabel} )
                                          </span>
                                        )}
                                      </span>
                                    </span>

                                    <span
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1e293b',
                                      }}
                                    >
                                      + {formatUsd(courierTotal)}
                                    </span>
                                  </div>
                                </>
                              )}

                              {/* Coupon Discount */}
                              {couponDiscount != null && couponDiscount !== '' && (
                                <>
                                  <span
                                    style={{
                                      height: '1px',
                                      width: '100%',
                                      backgroundColor: '#E5E7EB',
                                    }}
                                  />

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span
                                      style={{
                                        fontSize: '15px',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                      }}
                                    >
                                      Coupon Discount{' '}
                                      {appliedCoupon && (
                                        <span style={{ fontWeight: 600, color: '#DC2626' }}>
                                          ( {appliedCoupon} )
                                        </span>
                                      )}
                                    </span>

                                    <span
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#059669',
                                      }}
                                    >
                                      - USD {couponDiscount}
                                    </span>
                                  </div>
                                </>
                              )}

                              {/* Convenience Fee */}
                              <>
                                <span
                                  style={{
                                    height: '1px',
                                    width: '100%',
                                    backgroundColor: '#E5E7EB',
                                  }}
                                />

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span
                                    style={{ fontSize: '15px', color: '#1e293b', fontWeight: 600 }}
                                  >
                                    Convenience Fee
                                  </span>

                                  <span
                                    style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}
                                  >
                                    + USD {paymentDetails?.convenienceFee || '0.00'}
                                  </span>
                                </div>
                              </>

                              {/* Total */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginTop: '6px',
                                  paddingTop: '12px',
                                  borderTop: '1px dashed #cbd5e1',
                                }}
                              >
                                <span style={{ fontSize: '16px', fontWeight: 800, color: NAVY }}>
                                  Total
                                </span>

                                <span style={{ fontSize: '16px', fontWeight: 800, color: NAVY }}>
                                  {isPaymentCompleted(paymentDetails?.paymentDetails?.paymentStatus)
                                    ? formatUsd(summaryTotal)
                                    : '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                {/* Addresses Section */}
                {(paymentDetails?.pickupAddress || paymentDetails?.address) && (
                  <div>
                    <div>
                      {paymentDetails?.pickupAddress && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            padding: '14px',
                            marginTop: '20px',
                            borderRadius: '8px',
                            background: 'rgba(0,48,135,0.03)',
                            border: '1px solid rgba(0,48,135,0.1)',
                          }}
                        >
                          <div
                            style={{
                              marginBottom: '8px',
                              fontSize: '15px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              color: NAVY,
                            }}
                          >
                            Pickup Address
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '15px', color: '#94a3b8' }}>
                                Service Type
                              </span>
                              <span style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.pickupAddress?.serviceType}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '15px', color: '#94a3b8' }}>Address</span>
                              <span
                                style={{
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  color: '#1e293b',
                                  maxWidth: '60%',
                                  textAlign: 'right',
                                }}
                              >
                                {paymentDetails?.pickupAddress?.addressLine}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '15px', color: '#94a3b8' }}>Pincode</span>
                              <span style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.pickupAddress?.pinCode}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {paymentDetails?.address && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            padding: '14px',
                            borderRadius: '8px',
                            background: 'rgba(0,48,135,0.03)',
                            border: '1px solid rgba(0,48,135,0.1)',
                          }}
                        >
                          <div
                            style={{
                              marginBottom: '8px',
                              fontSize: '15px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              color: NAVY,
                            }}
                          >
                            Address
                          </div>
                          <div
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                Address Line
                              </span>
                              <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.address?.addressLine}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#94a3b8' }}>City</span>
                              <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.address?.city}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#94a3b8' }}>State</span>
                              <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.address?.state}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Country</span>
                              <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                {paymentDetails?.address?.country}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Plan and Add-Ons Section */}
                <>
                  <div>
                    {/* <div style={sectionHeaderStyle}>
                  <span style={sectionHeaderLabelStyle}>Selected Add-Ons</span>
                </div> */}

                    <div
                      style={{
                        display: 'flex',
                        gap: '15px',

                        flexDirection: 'column',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '15px',
                            marginTop: '20px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '12px 14px',
                              borderRadius: '10px',
                              background: 'rgba(99, 102, 241, 0.08)',
                              border: '1px solid rgba(99, 102, 241, 0.2)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '26px',
                                height: '26px',
                                borderRadius: '7px',
                                background: 'rgba(99, 102, 241, 0.15)',
                                flexShrink: 0,
                              }}
                            >
                              <FileText size={14} color="#4f46e5" strokeWidth={2.25} />
                            </div>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#1e293b',
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              Plan Name
                            </span>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 700,
                                color: '#0f172a',
                                flexShrink: 0,
                              }}
                            >
                              {paymentDetails?.pricingPlan?.title || '—'}
                            </span>
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '12px 14px',
                              borderRadius: '10px',
                              background: 'rgba(99, 102, 241, 0.08)',
                              border: '1px solid rgba(99, 102, 241, 0.2)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '26px',
                                height: '26px',
                                borderRadius: '7px',
                                background: 'rgba(99, 102, 241, 0.15)',
                                flexShrink: 0,
                              }}
                            >
                              <DollarSign size={14} color="#4f46e5" strokeWidth={2.25} />
                            </div>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#1e293b',
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              Price Per Member
                            </span>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 700,
                                color: '#0f172a',
                                flexShrink: 0,
                              }}
                            >
                              {paymentDetails?.pricingPlan?.discountedPricePerMember ||
                              paymentDetails?.pricingPlan?.discountedPrice
                                ? `USD ${
                                    paymentDetails.pricingPlan.discountedPricePerMember ||
                                    paymentDetails.pricingPlan.discountedPrice
                                  }`
                                : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {paymentDetails?.photoEnhancement?.selected && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 14px',
                            borderRadius: '10px',
                            background: 'rgba(168, 85, 247, 0.08)',
                            border: '1px solid rgba(168, 85, 247, 0.2)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: 'rgba(168, 85, 247, 0.15)',
                              flexShrink: 0,
                            }}
                          >
                            <ImagePlus size={14} color="#7c3aed" strokeWidth={2.25} />
                          </div>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Photo Enhancement
                          </span>
                          {paymentDetails?.photoEnhancement?.quantity && (
                            <span
                              style={{
                                fontSize: '14px',
                                fontWeight: 800,
                                color: '#7c3aed',
                                flexShrink: 0,
                              }}
                            >
                              {paymentDetails?.photoEnhancement?.quantity}x
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: '#0f172a',
                              flexShrink: 0,
                            }}
                          >
                            {formatUsd(photoEnhancementUnit)}
                          </span>
                        </div>
                      )}

                      {paymentDetails?.expedite?.selected && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 14px',
                            borderRadius: '10px',
                            background: 'rgba(245, 158, 11, 0.08)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: 'rgba(245, 158, 11, 0.18)',
                              flexShrink: 0,
                            }}
                          >
                            <Zap size={14} color="#b45309" strokeWidth={2.25} />
                          </div>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Expedite
                          </span>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: '#0f172a',
                              flexShrink: 0,
                            }}
                          >
                            {formatUsd(expeditePerMember)}
                            <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>
                              {' '}
                              / member
                            </span>
                          </span>
                        </div>
                      )}

                      {paymentDetails?.apostilled?.selected && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 14px',
                            borderRadius: '10px',
                            background: 'rgba(59, 130, 246, 0.08)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: 'rgba(59, 130, 246, 0.15)',
                              flexShrink: 0,
                            }}
                          >
                            <Stamp size={14} color="#1d4ed8" strokeWidth={2.25} />
                          </div>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {apostilleLabel}
                          </span>
                          {apostilleQuantity && (
                            <span
                              style={{
                                fontSize: '14px',
                                fontWeight: 800,
                                color: '#1d4ed8',
                                flexShrink: 0,
                              }}
                            >
                              {apostilleQuantity}x
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: '#0f172a',
                              flexShrink: 0,
                            }}
                          >
                            {formatUsd(apostilledUnitPrice)}
                          </span>
                        </div>
                      )}

                      {paymentDetails?.courierService?.selected && (
                        <div
                          style={{
                            gridColumn: 'span 1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 14px',
                            borderRadius: '10px',
                            background: 'rgba(16, 185, 129, 0.08)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: 'rgba(16, 185, 129, 0.15)',
                              flexShrink: 0,
                            }}
                          >
                            <Truck size={14} color="#047857" strokeWidth={2.25} />
                          </div>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Courier Service (VFS Roundtrip)
                          </span>
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: '#0f172a',
                              flexShrink: 0,
                            }}
                          >
                            {formatUsd(courierPerMember)}
                            <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>
                              {' '}
                              / member
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              </div>

              {/* {(currentUserRole === 'Accountant' ||
            (currentUserRole === 'Super Admin' && onPaymentStatusUpdate)) && (
            <div style={sectionDividerStyle}>
              <div style={sectionHeaderStyle}>
                <span style={sectionHeaderLabelStyle}>Admin Actions</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {currentUserRole === 'Super Admin' && onPaymentStatusUpdate && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div
                      style={{
                        minWidth: '140px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        Payment Status:
                      </span>
                    </div>
                    <Select
                      style={{ width: 300, fontSize: '16px' }}
                      value={pendingPaymentStatus}
                      onChange={value => setPendingPaymentStatus(value)}
                      options={paymentStatusSelectOptions}
                      size="middle"
                      placeholder="Select Payment Status"
                    />
                    <Button
                      type="primary"
                      onClick={() => onPaymentStatusUpdate(pendingPaymentStatus)}
                      loading={paymentStatusLoading}
                      style={{
                        fontSize: '16px',
                        height: '32px',
                        padding: '0 20px',
                        backgroundColor: NAVY,
                        borderColor: NAVY,
                        borderRadius: '8px',
                        fontWeight: 600,
                      }}
                    >
                      Update
                    </Button>
                  </div>
                )}

                {currentUserRole === 'Accountant' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div
                      style={{
                        minWidth: '140px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        Export
                      </span>
                    </div>
                    <Button
                      style={{
                        backgroundColor: NAVY,
                        color: 'white',
                        borderRadius: '8px',
                        height: '32px',
                        padding: '0 20px',
                        fontWeight: 600,
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                      onClick={() => handleDownloadForm(paymentDetails)}
                    >
                      Download
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )} */}
            </div>
          </div>
          {!hidePaymentVoucher && (
            <div style={{ marginTop: '20px' }}>
              <div style={cardStyle}>
                <PaymentVoucher
                  formDetails={formDetails}
                  embedded
                  refreshTrigger={voucherRefreshTrigger}
                />
              </div>
            </div>
          )}
        </HorizontalTabsSecPanel>
        {!hidePaymentVoucher && (
          <HorizontalTabsSecPanel id="payment-voucher" label="Payment Voucher">
            <div style={cardStyle}>
              <PaymentVoucher
                formDetails={formDetails}
                embedded
                refreshTrigger={voucherRefreshTrigger}
              />
            </div>
          </HorizontalTabsSecPanel>
        )}
        {orderRefund &&
          ['PENDING', 'APPROVED', 'REJECTED', 'REFUNDED'].includes(
            String(orderRefund.status || '')
              .trim()
              .toUpperCase()
          ) && (
            <HorizontalTabsSecPanel
              id="refund-request"
              label={
                <span>
                  Refund Request
                  {refundStatusBadge((orderRefund as RefundRequest).status)}
                </span>
              }
            >
              <div
                style={{
                  padding: '16px',
                  borderRadius: 8,
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div className="details-content">
                  <Card
                    title={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 16,
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                        >
                          <span style={inlineCardTitleStyle}>Refund Request Details</span>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '3px 4px 3px 10px',
                              borderRadius: 20,
                              background: '#eef2ff',
                              border: '1px solid #c7d2fe',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: '#3730a3',
                              }}
                            ></span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#1e1b4b',
                              }}
                            >
                              {inlineDisplayRefundId || '--'}
                            </span>
                            <button
                              type="button"
                              onClick={handleCopyRefundId}
                              title="Copy Refund ID"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 22,
                                height: 22,
                                padding: 0,
                                margin: 0,
                                borderRadius: '50%',
                                border: 'none',
                                background: '#4f46e5',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'background 0.15s ease',
                              }}
                              onMouseEnter={e =>
                                ((e.currentTarget as HTMLButtonElement).style.background =
                                  '#4338ca')
                              }
                              onMouseLeave={e =>
                                ((e.currentTarget as HTMLButtonElement).style.background =
                                  '#4f46e5')
                              }
                            >
                              <Copy size={11} strokeWidth={2.5} />
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#075dc5',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: '#53657f',
                                fontWeight: 600,
                              }}
                            >
                              Request Date:
                            </span>
                            <span>{inlineFormatDate(inlineCurrentRefund?.createdAt)}</span>
                          </div>

                          {/* Vertical Divider */}
                          {/* <span
                            style={{
                              display: 'block',
                              width: '1px',
                              height: '24px',
                              backgroundColor: '#c7d2fe',
                              flexShrink: 0,
                            }}
                          />

                          <div>
                            <div
                              style={{
                                padding: '4px 10px',
                                borderRadius: 4,
                                background:
                                  inlineCurrentRefund?.status === 'PENDING'
                                    ? '#fef3c7'
                                    : inlineCurrentRefund?.status === 'APPROVED'
                                      ? '#d1fae5'
                                      : inlineCurrentRefund?.status === 'REFUNDED'
                                        ? '#dbeafe'
                                        : '#fee2e2',
                                color:
                                  inlineCurrentRefund?.status === 'PENDING'
                                    ? '#d97706'
                                    : inlineCurrentRefund?.status === 'APPROVED'
                                      ? '#15803d'
                                      : inlineCurrentRefund?.status === 'REFUNDED'
                                        ? '#1d4ed8'
                                        : '#b91c1c',
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {inlineCurrentRefund?.status || 'PENDING'}
                            </div>
                          </div> */}
                        </div>
                      </div>
                    }
                    style={{ marginBottom: 16, borderRadius: 8 }}
                    bordered
                  >
                    <Row gutter={[48, 24]}>
                      <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Fee Type</div>
                          <div className="detail-value">
                            {inlineCurrentRefund?.refundType === 'GOVT_AND_COURIER'
                              ? 'Application Charges'
                              : inlineCurrentRefund?.refundType === 'DOCUMITRA_FEE'
                                ? 'Documitra Fee'
                                : inlineCurrentRefund?.refundType || '--'}
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Payment Date</div>
                          <div className="detail-value">
                            {inlineFormatDate(inlineCurrentRefund?.paymentDate)}
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Payment Method</div>
                          <div className="detail-value">
                            {inlineCurrentRefund?.paymentMethod || '--'}
                          </div>
                        </div>
                      </Col>
                      {(appliedCoupon || (couponDiscount != null && couponDiscount !== '')) && (
                        <>
                          <Col xs={24} sm={12} lg={6}>
                            <div className="detail-row">
                              <div className="detail-label">Coupon applied:</div>
                              <div className="detail-value">{appliedCoupon || '--'}</div>
                            </div>
                          </Col>
                          <Col xs={24} sm={12} lg={6}>
                            <div className="detail-row">
                              <div className="detail-label">Coupon Discount:</div>
                              <div className="detail-value">
                                {couponDiscount != null ? `USD ${couponDiscount}` : '--'}
                              </div>
                            </div>
                          </Col>
                        </>
                      )}
                      <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Amount Requested</div>
                          <div className="detail-value amount-requested">
                            ${inlineRequestedAmount.toFixed(2)} {inlineCurrency}
                          </div>
                        </div>
                      </Col>
                      {/* <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Service Type</div>
                          <div className="detail-value">
                            {inlineCurrentRefund?.applicationType || '--'}
                          </div>
                        </div>
                      </Col> */}
                      {/* <Col xs={24} sm={12} lg={6}>
                        <div className="detail-row">
                          <div className="detail-label">Request Date</div>
                          <div className="detail-value">
                            {inlineFormatDate(inlineCurrentRefund?.createdAt)}
                          </div>
                        </div>
                      </Col> */}
                      <Col xs={24}>
                        <div className="detail-row">
                          <div className="detail-label">Reason for Refund</div>
                          <div
                            className="detail-value"
                            style={{
                              padding: 8,
                              borderRadius: 4,
                              backgroundColor: '#F4F8FD',
                              border: '1px solid #d6e4f5',
                            }}
                          >
                            {inlineCurrentRefund?.refundReason || '--'}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {inlineShowApprovalReadOnly && (
                      <Divider orientation="left" orientationMargin={0}>
                        <span style={inlineCardTitleStyle}>Approval / Rejection</span>
                      </Divider>
                    )}

                    {inlineShowApprovalReadOnly && (
                      <Row gutter={[48, 24]}>
                        {inlineCurrentRefund?.status === 'REJECTED' ? (
                          <>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Status</div>
                                <span className="status-badge status-badge--rejected">
                                  Rejected
                                </span>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Rejected By</div>
                                <div className="detail-value">
                                  {inlineCurrentRefund?.rejectedBy || '--'}
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Rejection Date</div>
                                <div className="detail-value">
                                  {inlineFormatDate(inlineCurrentRefund?.rejectedAt)}
                                </div>
                              </div>
                            </Col>
                            <Col xs={24}>
                              <div className="detail-row">
                                <div className="detail-label">Remarks</div>
                                <div
                                  className="detail-value "
                                  style={{
                                    padding: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#F4F8FD',
                                    border: '1px solid #d6e4f5',
                                  }}
                                >
                                  {inlineCurrentRefund?.rejectionReason || '--'}
                                </div>
                              </div>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Status</div>
                                <span className="status-badge status-badge--approved">
                                  Approved
                                </span>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Approved By</div>
                                <div className="detail-value">
                                  {inlineCurrentRefund?.approvedBy || '--'}
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Approval Date</div>
                                <div className="detail-value">
                                  {inlineFormatDate(inlineCurrentRefund?.approvedAt)}
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div className="detail-row">
                                <div className="detail-label">Amount Approved</div>
                                <div className="detail-value amount-approved">
                                  $
                                  {Number(
                                    inlineCurrentRefund?.amountApproved ?? inlineRequestedAmount
                                  ).toFixed(2)}{' '}
                                  USD
                                </div>
                              </div>
                            </Col>
                            <Col xs={24}>
                              <div className="detail-row">
                                <div className="detail-label">Remarks</div>
                                <div
                                  className="detail-value"
                                  style={{
                                    padding: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#F4F8FD',
                                    border: '1px solid #d6e4f5',
                                  }}
                                >
                                  {inlineCurrentRefund?.approvalRemarks || '--'}
                                </div>
                              </div>
                            </Col>
                          </>
                        )}
                      </Row>
                    )}
                  </Card>

                  {inlineShowRefundedSection && (
                    <Card
                      title={
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 16,
                          }}
                        >
                          <span style={inlineCardTitleStyle}>Refunded</span>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#075dc5',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: '#53657f',
                                fontWeight: 600,
                              }}
                            >
                              Refund Date:
                            </span>
                            <span>
                              {inlineFormatDate(
                                inlineCurrentRefund?.refundDetails?.refundDate ||
                                  inlineCurrentRefund?.refundedAt
                              )}
                            </span>
                          </div>
                        </div>
                      }
                      style={{ marginBottom: 16, borderRadius: 8 }}
                      bordered
                    >
                      <Row gutter={[48, 24]}>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Refund Amount</div>
                            <div className="detail-value amount-requested">
                              $
                              {Number(
                                inlineCurrentRefund?.refundDetails?.refundAmount ??
                                  inlineCurrentRefund?.refundAmount ??
                                  0
                              ).toFixed(2)}{' '}
                              USD
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Refund Method</div>
                            <div className="detail-value">
                              {inlineCurrentRefund?.refundDetails?.refundMethod || '--'}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Refunded By</div>
                            <div className="detail-value">
                              {inlineCurrentRefund?.refundDetails?.refundedBy || '--'}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <div className="detail-row">
                            <div className="detail-label">Confirmation Document</div>
                            <div className="detail-value">
                              {inlineCurrentRefund?.refundDetails?.confirmationDocumentUrl ||
                              inlineCurrentRefund?.refundDetails?.confirmationDocumentName ? (
                                <a
                                  href={
                                    inlineCurrentRefund?.refundDetails?.confirmationDocumentUrl ||
                                    '#'
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    color: '#075dc5',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                  }}
                                >
                                  <FileText size={14} />
                                  {inlineCurrentRefund?.refundDetails?.confirmationDocumentName ||
                                    'View Document'}
                                </a>
                              ) : (
                                '--'
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="detail-row">
                            <div className="detail-label">Remarks</div>
                            <div
                              className="detail-value"
                              style={{
                                padding: 8,
                                borderRadius: 4,
                                backgroundColor: '#F4F8FD',
                                border: '1px solid #d6e4f5',
                              }}
                            >
                              {inlineCurrentRefund?.refundDetails?.notes || '--'}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  )}

                  <RefundTrackingHistory
                    refund={inlineCurrentRefund}
                    currency={inlineCurrency}
                    requestedAmount={inlineRequestedAmount}
                  />
                </div>
              </div>
            </HorizontalTabsSecPanel>
          )}
      </HorizontalTabsSec>
    </div>
  );
};

export default PaymentDetails;
